import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE } from "../config/site";

/**
 * Per-route document head: title, description, canonical, Open Graph, Twitter
 * and JSON-LD.
 *
 * Every public page renders exactly one of these. react-helmet-async merges in
 * mount order, so a page-level tag wins over anything set higher up.
 *
 * `jsonLd` takes a single object or an array; nullish entries are dropped, so
 * callers can pass e.g. `faqSchema(faqs)` without guarding for an empty list.
 * Each block is emitted as its own <script> rather than being merged into a
 * @graph, which keeps a malformed block from invalidating the others.
 */
const SEO = ({
  title,
  description,
  path = "/",
  image,
  noindex = false,
  type = "website",
  jsonLd,
}) => {
  const url = `${SITE_URL}${path}`;

  // Avoids "Five Lights Reno | Five Lights Reno" when a page title already
  // carries the brand.
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

  // Bundled imports resolve to root-relative build paths ("/static/media/…"),
  // but OG and Twitter crawlers require absolute URLs.
  const resolvedImage = image
    ? image.startsWith("http")
      ? image
      : `${SITE_URL}${image}`
    : DEFAULT_OG_IMAGE;

  const blocks = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean);

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <link rel="canonical" href={url} />
      )}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_CA" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={resolvedImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={resolvedImage} />

      {blocks.map((block, index) => (
        <script type="application/ld+json" key={index}>
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
